/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { formatDurationText } from '@/utils/formatDurationText';
import { iconMap } from '@/ux-common/src/lib/helper';
import { formatNumberByLocale } from '@/ux-common/src/lib/utils';
import { format, parseISO } from 'date-fns';
import { IPrompt, PromptsData } from 'types/prompt';
import { FactsData } from 'types/traces';

import { formatSecondsAndMilliseconds, getRoleAndContent } from '@/lib/helper';
import { cn } from '@/lib/utils';

import { DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DisplayGanttChart } from './display-chart';
import { MultiplePromptDisplay } from './multiple-prompt-display';
import { OutputPromptDispaly } from './output-prompt-display';
import { ComparePrompts } from './prompt-compare';
import PromptDetailsFooter from './prompt-details-modal-footer';

function dislayOutputPromptWithRole(prompt: IPrompt) {
  if (!prompt || prompt['data.output']?.response === '') return '';

  const { outputRole, outputString } = getRoleAndContent(prompt);

  return (
    <OutputPromptDispaly outputRole={outputRole} outputString={outputString} />
  );
}

export const PromptDetailsModal = ({
  position,
  data,
  factsData,
  compare,
  handlePrev,
  handleNext,
}: {
  position?: number;
  data: PromptsData;
  factsData: FactsData[];
  compare?: boolean;
  handlePrev?: () => void;
  handleNext?: () => void;
}) => {
  const { messages } = useLanguage();
  const headerItems = messages?.General;
  const rowHeader = [
    headerItems?.input,
    headerItems?.output,
    headerItems?.model,
    headerItems?.service,
    headerItems?.provider,
    messages?.PromptsListing?.row_header?.tokens,
    headerItems?.duration,
  ];

  const searchParams = useSearchParams();
  const factName =
    searchParams.get('factName') || searchParams.get('fact_name');

  const shouldShowNarrativeView = factsData?.find(
    (fact: FactsData) => fact.key === factName && fact.isGraphSupported
  )?.isGraphSupported;

  const findItem = (rowHeader: string, prompt: IPrompt) => {
    let value;
    let start;
    let end;
    let duration;

    if (!prompt) return;

    switch (rowHeader) {
      case headerItems?.input:
        value = <MultiplePromptDisplay inputData={prompt} isDetails />;
        break;
      case headerItems?.output:
        value = dislayOutputPromptWithRole(prompt);
        break;
      case headerItems?.model:
        value = prompt?.model_name;
        break;
      case headerItems?.service:
        value = prompt?.model_attributes?.service_name;
        break;
      case headerItems?.provider:
        value = prompt?.model_attributes?.provider_name;
        break;
      case messages?.PromptsListing?.row_header?.tokens:
        value = formatNumberByLocale(prompt?.model_attributes?.total_tokens);
        break;
      case headerItems?.duration: {
        start = new Date(prompt.start_time).valueOf();
        end = new Date(prompt.end_time).valueOf();
        duration = formatSecondsAndMilliseconds(end - start);

        const parsedStartDate = parseISO(prompt.start_time);
        const formattedStartDate = format(
          parsedStartDate,
          'dd MMM h:mm:ss.SSSa'
        );
        const parsedEndDate = parseISO(prompt.end_time);
        const formattedEndDate = format(parsedEndDate, 'dd MMM h:mm:ss.SSSa');

        value = `${duration} (${formattedStartDate} - ${formattedEndDate})`;
        break;
      }

      default:
        value = 'N/A';
    }
    return value;
  };

  return (
    <DialogContent
      className="flex max-h-[80%] max-w-[960px] flex-col gap-0 overflow-hidden border-link-active bg-gradient-sky-dark p-[30px]"
      onInteractOutside={(e) => {
        e.preventDefault();
      }}
    >
      <DialogTitle className={`shrink-0 text-[24px] font-normal leading-7`}>
        {compare
          ? `${messages?.PromptsListing.compare}`
          : messages?.PromptsListing?.row_header?.prompt_details}
      </DialogTitle>
      <DialogDescription
        asChild
        className="mt-4 flex min-h-0 flex-1 flex-col border-b border-line-primary pb-2.5"
      >
        <Tabs defaultValue="prompts">
          {!compare && (
            <TabsList className="flex w-full shrink-0 justify-start gap-8 rounded-none border-b border-line-primary bg-transparent p-0">
              <TabsTrigger
                value="prompts"
                className="rounded-none border-b-2 border-b-transparent px-0 pb-3 text-h4 font-medium text-link-active hover:border-b-link-active data-[state=active]:border-b-body-primary data-[state=active]:bg-transparent data-[state=active]:text-body-primary"
              >
                {messages?.General?.prompts}
              </TabsTrigger>
              <TabsTrigger
                value="traces"
                className="rounded-none border-b-2 border-b-transparent px-0 pb-3 text-h4 font-medium text-link-active hover:border-b-link-active data-[state=active]:border-b-body-primary data-[state=active]:bg-transparent data-[state=active]:text-body-primary"
              >
                {messages?.General?.traces}
              </TabsTrigger>
            </TabsList>
          )}
          <TabsContent
            value="prompts"
            className={`min-h-0 flex-1 overflow-auto break-words break-all rounded-sm ${
              compare
                ? 'border-t border-t-line-primary pt-3'
                : 'mt-2.5 border border-line-secondary'
            }`}
          >
            {compare ? (
              <ComparePrompts
                data={data}
                rowHeader={rowHeader}
                findItem={findItem || undefined}
              />
            ) : (
              rowHeader.map((item, index) => (
                <div key={item} className="flex ">
                  <div
                    className={cn(
                      'w-[200px] min-w-36 bg-[#093242] p-3 text-body-subtle',
                      item === headerItems?.input && 'rounded-tl-sm',
                      item ===
                        messages?.PromptsListing?.row_header?.prompt_details &&
                        'rounded-bl-sm',
                      index !== 0 &&
                        'border border-x-0 border-b-0 border-t-line-secondary'
                    )}
                  >
                    {item}
                  </div>
                  <div
                    className={cn(
                      'flex w-full items-center gap-2 bg-gradient-sky-dark p-3 text-body-primary',
                      item === headerItems?.input && 'rounded-tr-sm',
                      item ===
                        messages?.PromptsListing?.row_header?.prompt_details &&
                        'rounded-br-sm',
                      index !== 0 &&
                        'border border-x-0 border-b-0 border-t-line-secondary'
                    )}
                  >
                    {item === headerItems?.provider &&
                    findItem(item!, data[position!]) ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                        <Image
                          className="size-6"
                          src={
                            iconMap[
                              data[position!]?.model_attributes?.provider_type
                            ] ?? iconMap['default']
                          }
                          alt={`${
                            data[position!]?.model_attributes?.provider_type ??
                            'default'
                          } provider`}
                          width={100}
                          height={100}
                        />
                      </div>
                    ) : item === headerItems?.service &&
                      findItem(item!, data[position!]) ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                        <Image
                          className="size-6"
                          src={
                            iconMap[
                              data[position!]?.model_attributes?.service_type
                            ] ?? iconMap['default']
                          }
                          alt={`${
                            data[position!]?.model_attributes?.service_type ??
                            'default'
                          } service`}
                          width={100}
                          height={100}
                        />
                      </div>
                    ) : (
                      ''
                    )}
                    {item === `${messages?.General?.duration}`
                      ? formatDurationText(
                          findItem(item!, data[position!]) as string
                        )
                      : findItem(item!, data[position!])}{' '}
                  </div>
                </div>
              ))
            )}
          </TabsContent>
          <TabsContent
            value="traces"
            className="min-h-0 w-[890px] flex-1 overflow-auto"
          >
            {position !== undefined && (
              <DisplayGanttChart
                trace_id={data[position]?.trace_id}
                span_id={data[position]?.span_id}
                workflow_name={data[position]?.workflow_name}
                shouldShowNarrativeView={shouldShowNarrativeView}
              />
            )}
          </TabsContent>
        </Tabs>
      </DialogDescription>
      <div className="shrink-0">
        <PromptDetailsFooter
          compare={compare}
          position={position}
          dataLength={data.length}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </div>
    </DialogContent>
  );
};
